import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { Plus, Play, Pause, Trash2, PlayCircle, Search, X, ChevronDown, Check, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useApiLoading, LoadingRow } from "../components/TableLoader";

const FREQUENCIES = [
    { key: "daily",   label: "Daily" },
    { key: "weekly",  label: "Weekly" },
    { key: "monthly", label: "Monthly" },
    { key: "custom",  label: "Custom (cron)" },
];

/** Render an ISO/UTC datetime in the given IANA timezone, e.g. "16 Jul 2026, 10:50 (Asia/Calcutta)".
 *
 * FastAPI returns naive ISO strings ("2026-07-16T05:20:00") which JS otherwise interprets as
 * BROWSER-local. All backend datetimes here are stored in UTC, so we force UTC interpretation
 * by appending 'Z' when the ISO string has no timezone suffix.
 */
function formatInTz(iso, tz) {
    if (!iso) return "—";
    try {
        // If the ISO has no timezone marker (no 'Z' or ±HH:MM offset), treat it as UTC.
        const hasTz = /[zZ]$|[+\-]\d{2}:?\d{2}$/.test(iso);
        const utcIso = hasTz ? iso : `${iso}Z`;
        const dt = new Date(utcIso);
        const fmt = new Intl.DateTimeFormat("en-GB", {
            timeZone: tz || "UTC",
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit", hour12: false,
        });
        return `${fmt.format(dt)} (${tz || "UTC"})`;
    } catch {
        return new Date(iso).toLocaleString();
    }
}

export default function Schedules() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const apiLoading = useApiLoading();
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [timezones, setTimezones] = useState([]);
    const [saving, setSaving] = useState(false);
    const emptyForm = () => ({
        template_ids: [],
        frequency: "daily",
        cron_expression: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        run_time: "09:00",
        recipients: user?.email || "",
    });
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        try {
            const { data } = await api.get("/schedules");
            setList(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load schedules");
        }
    };
    useEffect(() => { load(); }, []);

    const openModal = async (schedule = null) => {
        setEditingId(schedule?.id || null);
        setForm(schedule ? {
            template_ids: schedule.template_ids || (schedule.template_id ? [schedule.template_id] : []),
            frequency: schedule.frequency,
            cron_expression: schedule.cron_expression || "",
            timezone: schedule.timezone || "UTC",
            run_time: schedule.run_time || "09:00",
            recipients: schedule.recipients || "",
        } : emptyForm());
        setOpen(true);
        try {
            const [t, tz] = await Promise.all([
                api.get("/report-templates"),
                api.get("/schedules/timezones"),
            ]);
            setTemplates(t.data || []);
            setTimezones(tz.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load picklists");
        }
    };

    const pager = usePagination(list, 25);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.template_ids.length) { toast.error("Pick at least one template"); return; }
        if (form.frequency === "custom" && !form.cron_expression.trim()) { toast.error("Enter a cron expression for custom frequency"); return; }
        setSaving(true);
        try {
            const payload = {
                template_ids: form.template_ids,
                frequency: form.frequency,
                cron_expression: form.frequency === "custom" ? form.cron_expression.trim() : null,
                timezone: form.timezone,
                run_time: form.run_time,
                recipients: form.recipients,
            };
            if (editingId) {
                await api.patch(`/schedules/${editingId}`, payload);
                toast.success("Schedule updated");
            } else {
                await api.post("/schedules", payload);
                toast.success("Schedule created");
            }
            setOpen(false);
            setEditingId(null);
            setForm(emptyForm());
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || (editingId ? "Failed to update schedule" : "Failed to create schedule"));
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (id) => { await api.post(`/schedules/${id}/toggle`); load(); };
    const remove = async (id) => {
        if (!window.confirm("Delete this schedule?")) return;
        await api.delete(`/schedules/${id}`);
        load();
    };
    const runNow = async (id) => {
        try {
            const { data } = await api.post(`/schedules/${id}/run`);
            toast.success(`Ran schedule · ${data.rows} rows · ${data.status}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Run failed");
        }
    };

    return (
        <div data-testid="schedules-page">
            <PageHeader
                eyebrow="Automation"
                title="Scheduled reports"
                subtitle="Pick a template, choose a cadence and timezone, and we'll email the report as an attachment on schedule."
                actions={
                    <button onClick={() => openModal()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="schedule-create-btn">
                        <Plus className="h-4 w-4" /> New schedule
                    </button>
                }
            />

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3">Templates</th>
                            <th className="overline text-left p-3">Frequency</th>
                            <th className="overline text-left p-3">Timezone</th>
                            <th className="overline text-left p-3">Recipients</th>
                            <th className="overline text-left p-3">Next run</th>
                            <th className="overline text-left p-3">Status</th>
                            <th className="overline text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pager.slice.map((s) => (
                            <tr key={s.id} className="border-b border-zinc-200 last:border-0">
                                <td className="p-3 text-zinc-900 truncate max-w-[240px]" title={(s.template_names || (s.template_name ? [s.template_name] : [])).join(", ") || s.report_type || "—"}>
                                    {s.template_names?.length ? (
                                        s.template_names.length === 1 ? s.template_names[0] : (
                                            <span>{s.template_names[0]} <span className="text-[10px] text-zinc-400">+{s.template_names.length - 1} more</span></span>
                                        )
                                    ) : (s.template_name || <span className="text-zinc-400 italic">{s.report_type || "—"}</span>)}
                                </td>
                                <td className="p-3 capitalize text-zinc-700">
                                    {s.frequency === "custom" ? <><code className="text-xs">{s.cron_expression}</code></> : <>{s.frequency} · {s.run_time}</>}
                                </td>
                                <td className="p-3 text-zinc-600 text-xs">{s.timezone || "UTC"}</td>
                                <td className="p-3 text-zinc-500 text-xs truncate max-w-[200px]" title={s.recipients}>{s.recipients}</td>
                                <td className="p-3 text-zinc-500 text-xs" title={s.next_run ? `UTC: ${new Date(s.next_run).toISOString().replace('T', ' ').slice(0, 16)}` : ""}>{formatInTz(s.next_run, s.timezone)}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${s.is_active ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20" : "bg-zinc-200 text-zinc-500"}`}>
                                        {s.is_active ? "active" : "paused"}
                                    </span>
                                </td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex items-center gap-2 text-xs">
                                        <button onClick={() => runNow(s.id)} className="inline-flex items-center gap-1 text-zinc-600 hover:wl-accent-text" data-testid={`run-${s.id}`}><PlayCircle className="h-3.5 w-3.5" />run</button>
                                        <button onClick={() => openModal(s)} className="inline-flex items-center gap-1 text-zinc-600 hover:wl-accent-text" data-testid={`edit-${s.id}`}><Pencil className="h-3.5 w-3.5" />edit</button>
                                        <button onClick={() => toggle(s.id)} className="inline-flex items-center gap-1 text-zinc-600 hover:wl-accent-text" data-testid={`toggle-${s.id}`}>{s.is_active ? <><Pause className="h-3.5 w-3.5" />pause</> : <><Play className="h-3.5 w-3.5" />resume</>}</button>
                                        <button onClick={() => remove(s.id)} className="inline-flex items-center gap-1 text-red-500 hover:text-red-400" data-testid={`delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!list.length && (apiLoading ? <LoadingRow colSpan={7} /> : <tr><td colSpan={7} className="p-10 text-center text-zinc-500">No schedules yet.</td></tr>)}
                    </tbody>
                </table>
                {list.length > 0 && (
                    <Pagination
                        page={pager.page}
                        totalPages={pager.totalPages}
                        pageSize={pager.pageSize}
                        range={pager.range}
                        setPage={pager.setPage}
                        setPageSize={pager.setPageSize}
                        dataTestId="schedules-pagination"
                    />
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setOpen(false); setEditingId(null); }}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-zinc-50 border border-zinc-200 w-full max-w-lg p-6 rounded-md" data-testid="schedule-create-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl">{editingId ? "Edit schedule" : "New schedule"}</h3>
                            <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="p-1 text-zinc-500 hover:text-zinc-900" aria-label="Close"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="overline">Templates</label>
                                <TemplateMultiSelect
                                    options={templates}
                                    selectedIds={form.template_ids}
                                    onChange={(ids) => setForm({ ...form, template_ids: ids })}
                                />
                                {templates.length === 0 && <div className="text-[10px] text-amber-600 mt-1">You haven&apos;t created any templates yet. Create one from the Templates page first.</div>}
                                {form.template_ids.length > 1 && (
                                    <div className="text-[10px] text-zinc-400 mt-1">
                                        {form.template_ids.length} templates selected — they&apos;ll all run at each scheduled tick and land in the same email as attachments.
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="overline">Frequency</label>
                                    <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded" data-testid="schedule-frequency-select">
                                        {FREQUENCIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                                    </select>
                                </div>
                                {form.frequency !== "custom" && (
                                    <div>
                                        <label className="overline">Time</label>
                                        <input type="time" value={form.run_time} onChange={(e) => setForm({ ...form, run_time: e.target.value })} className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded" data-testid="schedule-time-input" />
                                    </div>
                                )}
                            </div>
                            {form.frequency === "custom" && (
                                <div>
                                    <label className="overline">Cron expression</label>
                                    <input value={form.cron_expression} onChange={(e) => setForm({ ...form, cron_expression: e.target.value })} placeholder="e.g. 0 9 * * MON — every Monday at 09:00" className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded font-mono text-sm" data-testid="schedule-cron-input" />
                                    <div className="text-[10px] text-zinc-400 mt-1">Standard 5-field cron. Times are interpreted in the selected timezone.</div>
                                </div>
                            )}
                            <div>
                                <label className="overline">Timezone</label>
                                <TimezonePicker value={form.timezone} options={timezones} onChange={(tz) => setForm({ ...form, timezone: tz })} />
                            </div>
                            <div>
                                <label className="overline">Recipients (comma-separated emails)</label>
                                <input required value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="ops@example.com, cs@example.com" className="w-full mt-1 px-3 py-2 bg-white border border-zinc-200 rounded" data-testid="schedule-recipients-input" />
                                <div className="text-[10px] text-zinc-400 mt-1">Each schedule tick, the generated report file(s) will be attached to the email.</div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={() => { setOpen(false); setEditingId(null); }} className="px-3 py-2 text-sm text-zinc-500">Cancel</button>
                            <button type="submit" disabled={saving} className="px-4 py-2 rounded text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="schedule-submit-btn">
                                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                                {editingId ? "Save changes" : "Schedule"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function TemplateMultiSelect({ options, selectedIds, onChange }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (!open) { setQ(""); return; }
        const t = setTimeout(() => searchRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const filtered = useMemo(() => {
        const n = q.trim().toLowerCase();
        if (!n) return options;
        return options.filter((o) => (o.name || "").toLowerCase().includes(n) || (o.platform || "").toLowerCase().includes(n));
    }, [q, options]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const summary = selectedIds.length === 0
        ? "Pick one or more templates…"
        : selectedIds.length === 1
            ? (options.find((o) => o.id === selectedIds[0])?.name || "1 selected")
            : `${selectedIds.length} templates selected`;

    const toggleOne = (id) => {
        onChange(selectedSet.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    };
    const clearAll = (e) => { e.stopPropagation(); onChange([]); };
    const selectAllFiltered = () => onChange(Array.from(new Set([...selectedIds, ...filtered.map((f) => f.id)])));

    return (
        <div className="relative mt-1" ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded text-left text-sm hover:border-zinc-400"
                data-testid="schedule-templates-trigger"
                title={selectedIds.length ? selectedIds.map((id) => options.find((o) => o.id === id)?.name).filter(Boolean).join(", ") : ""}
            >
                <span className={`flex-1 truncate ${selectedIds.length === 0 ? "text-zinc-400" : "text-zinc-900"}`}>{summary}</span>
                {selectedIds.length > 0 && (
                    <span role="button" tabIndex={0} onClick={clearAll} onKeyDown={(e) => { if (e.key === "Enter") clearAll(e); }} className="text-[10px] uppercase text-zinc-400 hover:text-red-500" data-testid="schedule-templates-clear">clear</span>
                )}
                <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded shadow-lg" data-testid="schedule-templates-popover">
                    <div className="p-2 border-b border-zinc-100 flex items-center gap-2">
                        <Search className="h-4 w-4 text-zinc-400" />
                        <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" className="flex-1 text-sm outline-none bg-transparent" data-testid="schedule-templates-search" />
                        {q && filtered.length > 0 && (
                            <button type="button" onClick={selectAllFiltered} className="text-[10px] uppercase text-zinc-500 hover:wl-accent-text">Select all</button>
                        )}
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                        {options.length === 0 && <div className="text-xs text-zinc-400 italic p-3">No templates available. Create one from the Templates page.</div>}
                        {options.length > 0 && filtered.length === 0 && <div className="text-xs text-zinc-400 italic p-3">No matches.</div>}
                        {filtered.map((o) => {
                            const active = selectedSet.has(o.id);
                            return (
                                <button
                                    type="button"
                                    key={o.id}
                                    onClick={() => toggleOne(o.id)}
                                    title={o.name}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-zinc-50 ${active ? "bg-orange-50/60" : ""}`}
                                    data-testid={`schedule-template-option-${o.id}`}
                                >
                                    <span className={`h-4 w-4 flex items-center justify-center rounded border ${active ? "bg-[var(--wl-primary-hex)] border-[var(--wl-primary-hex)]" : "border-zinc-300 bg-white"}`}>
                                        {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                    </span>
                                    <span className="flex-1 truncate">{o.name}</span>
                                    <span className="text-[10px] text-zinc-400 uppercase">{o.platform}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}


function TimezonePicker({ value, options, onChange }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (!open) { setQ(""); return; }
        const t = setTimeout(() => searchRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const filtered = useMemo(() => {
        const n = q.trim().toLowerCase();
        if (!n) return options.slice(0, 400);
        return options.filter((o) => o.toLowerCase().includes(n)).slice(0, 400);
    }, [q, options]);

    return (
        <div className="relative mt-1" ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded text-left text-sm hover:border-zinc-400"
                data-testid="schedule-timezone-trigger"
            >
                <span className="flex-1 truncate text-zinc-900">{value || "UTC"}</span>
                <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded shadow-lg" data-testid="schedule-timezone-popover">
                    <div className="p-2 border-b border-zinc-100 flex items-center gap-2">
                        <Search className="h-4 w-4 text-zinc-400" />
                        <input
                            ref={searchRef}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search timezone…"
                            className="flex-1 text-sm outline-none bg-transparent"
                            data-testid="schedule-timezone-search"
                        />
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                        {options.length === 0 && (
                            <div className="text-xs text-zinc-400 italic p-3">Loading timezones…</div>
                        )}
                        {filtered.length === 0 && options.length > 0 && (
                            <div className="text-xs text-zinc-400 italic p-3">No matches.</div>
                        )}
                        {filtered.map((o) => (
                            <button
                                type="button"
                                key={o}
                                onClick={() => { onChange(o); setOpen(false); }}
                                className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-zinc-50 ${o === value ? "bg-orange-50/60" : ""}`}
                                data-testid={`schedule-timezone-option-${o.replace(/\//g, "_")}`}
                            >
                                <span className="flex-1 truncate">{o}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
